/**
 * English — the primary UI language. Every string the Studio says is authored here first;
 * `tr.ts` mirrors it, and a test asserts the two key sets match exactly.
 *
 * `{name}` placeholders are filled by `t(key, vars)`. Plurals are separate keys rather
 * than a plural engine: two languages, a handful of counted strings, and Turkish has one
 * plural form — a full ICU runtime would be more machinery than meaning.
 *
 * This prompt authors **shell chrome only** (nav, the top-bar cluster, the floor, the rail's
 * heads, sign-in, the account menu). The `surface.*` block is placeholder titling for the
 * routed frames; `studio/02`–`05` replace it with each surface's real copy.
 */

export const en = {
  // ── the app frame ─────────────────────────────────────────────────────────
  'app.skipToMain': 'Skip to the main content',
  'app.opening': 'Opening the Studio…',
  'app.mainLabel': 'Studio surface',
  'narrow.title': 'The Studio needs a wider screen',
  'narrow.body':
    'This is a workshop, not a feed — drawing, dragging, and a grid you need to see all of at once. It opens on a tablet or a laptop. Come back on one and everything will be where you left it.',

  // ── top bar ───────────────────────────────────────────────────────────────
  'nav.sections': 'Studio sections',
  'nav.library': 'Library',
  'nav.stories': 'Stories',
  'nav.browse': 'Browse',

  // the bare cluster: what's happening, what it costs, who you are
  'cluster.making': '{count} making',
  'cluster.makingNothing': 'Nothing being made',
  'cluster.generations': 'Generations',
  // Config-null unless this family has been given an allowance of its own. There is no
  // number to print until then, and the Studio does not invent one.
  'cluster.generationsUnset': 'not set yet',
  // A reading, counting up. Never "N left", never a countdown — the honesty rule.
  'cluster.generationsUsed': '{used} of {cap}',

  // ── the rail ──────────────────────────────────────────────────────────────
  'rail.label': 'Contextual rail',
  'rail.shelfTitle': 'Your library',
  'rail.search': 'Search your library',
  'rail.searchPlaceholder': 'Search',
  'rail.siblingsTitle': 'Alongside this',
  'rail.back': 'back to your library',
  'rail.stateTitle': "What's being made",
  'rail.stateNeedsYou': 'What needs you',
  'rail.stateNothing': 'Nothing is being made right now.',
  'rail.sourceTitle': 'Pull from your library',
  'rail.empty': 'Nothing here yet.',

  // the four kinds, as the rail names them (plural)
  'kind.characters': 'Characters',
  'kind.settings': 'Settings',
  'kind.props': 'Props',
  'kind.beats': 'Beats',

  // ── the constant floor: what needs you + any LOCAL cost ───────────────────
  'floor.label': 'What needs you',
  'floor.nothing': 'Nothing needs you',
  'floor.needsOkOne': '1 needs your OK',
  'floor.needsOkMany': '{count} need your OK',
  'floor.review': 'review',

  // ── account menu ──────────────────────────────────────────────────────────
  'account.label': 'Account · {name}',
  'account.menu': 'Account',
  'account.signedInAs': 'Signed in as {email}',
  'account.signOut': 'Sign out',
  'account.language': 'Language',
  'account.languageEn': 'English',
  'account.languageTr': 'Türkçe',
  'account.theme': 'Theme',
  'account.themeSystem': 'System',
  'account.themeLight': 'Day',
  'account.themeDark': 'Night',
  'account.methods': 'Signs in with {methods}',
  'account.methodLink': 'email link',
  'account.methodPassword': 'password',
  'account.methodGoogle': 'Google',
  'account.methodEmail': 'email',
  'account.verifyNote': 'Verify your email — we sent a link.',
  // The consequence, stated rather than left to be discovered (`studio/07` q1, ruled in
  // DEFERRED). Firebase's own behaviour: a Google sign-in onto an address whose password
  // was never verified drops the password provider. Nothing is blocked on it — the note
  // says what will happen, and the parent decides.
  'account.verifyConsequence':
    'Until you do, signing in with Google for this address will replace your password.',
  'account.verifyResend': 'Send it again',
  'account.verifyDismiss': 'Not now',
  'account.verifySent': 'Verification email sent.',
  'account.changePassword': 'Change password',
  'account.setPassword': 'Set a password',

  // ── sign-in — an email link, an email + password, or Google (PRD 04 §3) ──
  'signin.title': 'Come in',
  'signin.lead':
    'Tell us your email, then choose a way in: a link we send you, a password, or your Google account.',
  'signin.email': 'Email',
  'signin.emailPlaceholder': 'you@example.com',
  'signin.send': 'Send me a link',
  'signin.sending': 'Sending…',
  'signin.usePassword': 'Use a password',
  'signin.password': 'Password',
  'signin.passwordRequired': 'Your password, please.',
  'signin.passwordTooShort': 'At least 8 characters.',
  'signin.signIn': 'Sign in',
  'signin.create': 'Create an account',
  'signin.forgot': 'Forgot it?',
  'signin.working': 'One moment…',
  'signin.or': 'or',
  'signin.google': 'Continue with Google',
  'signin.sentTitle': 'Check your email',
  'signin.sentBody': 'We sent a sign-in link to {email}. Open it on this device.',
  'signin.sentAgain': 'Use a different email',
  'signin.resetTitle': 'Check your email',
  'signin.resetBody':
    'We sent a password reset link to {email}. If there is no account for that address, nothing will arrive.',
  'signin.back': 'Back',
  'signin.finishing': 'Signing you in…',
  'signin.confirmEmailTitle': 'One more thing',
  'signin.confirmEmailBody':
    'You opened the link on a different device or browser. Type the email you asked for the link with.',
  'signin.confirmEmailAction': 'Finish signing in',
  'signin.offerPassword': 'Use your password instead.',
  'signin.offerLink': 'Send me a link instead.',
  'signin.emailRequired': 'An email address, please.',
  'signin.failed': "That didn't work. Try again.",
  'signin.emulator': 'Local build — the link is in the Auth emulator at {url}.',

  // the error matrix — honest, and never a directory of who has an account
  'signin.errBadCredentials':
    "That email and password don't match — or there's no account yet. Try again, reset it, or create one.",
  'signin.errEmailInUse':
    'There is already an account for that email. Sign in with its password, or reset it.',
  'signin.errWeakPassword': 'A longer password, please — at least 8 characters.',
  'signin.errTooMany': 'Too many tries. Wait a moment, then try again.',
  'signin.errNetwork': "We couldn't reach the sign-in service. Check the connection and try again.",
  'signin.errInvalidEmail': "That doesn't look like an email address.",
  'signin.errPopup': 'The Google window was closed or blocked. Try again.',
  'signin.errMethodOff': "That way in isn't switched on. Try another.",
  'signin.errRecentLogin': 'Sign in again first, then try once more.',
  'signin.errAccountExistsLink': 'That address already comes in with an email link.',
  'signin.errAccountExistsPassword': 'That address already comes in with a password.',

  // ── the account's password (account menu dialogs) ─────────────────────────
  'password.changeTitle': 'Change your password',
  'password.setTitle': 'Set a password',
  'password.setBody':
    'A password beside the way you already come in. Your link and your Google account keep working.',
  'password.current': 'Current password',
  'password.next': 'New password',
  'password.nextHelp': 'At least 8 characters.',
  'password.required': 'Your current password, please.',
  'password.confirm': 'Save',
  'password.cancel': 'Cancel',
  'password.working': 'Saving…',
  'password.changed': 'Password changed.',
  'password.set': 'Password set.',
  'password.errCurrent': "That isn't your current password.",
  'password.errRecentLogin':
    'Firebase wants a fresh sign-in for this. Sign out, come back in with a new link, and try again.',

  // ── derived status — the one table (see data/status.ts) ───────────────────
  'status.none': 'No reference yet',
  'status.making': 'Making',
  'status.needsOk': 'Needs your OK',
  'status.ready': 'Ready',
  'status.readyLocked': 'Ready · Locked',
  'status.error': "Didn't come back",
  'status.nothingMadeYet': 'Nothing made yet',
  'status.noImage': 'no image',
  'status.modelSheetMin': 'model sheet · ~1 min',

  // ── the four kinds, singular, as a card names itself ──────────────────────
  'kind.character': 'Character',
  'kind.setting': 'Setting',
  'kind.prop': 'Prop',
  'kind.beat': 'Beat',
  'kind.characterArchived': 'Character · archived',
  'kind.settingArchived': 'Setting · archived',
  'kind.propArchived': 'Prop · archived',
  'kind.beatArchived': 'Beat · archived',

  // ── library home ──────────────────────────────────────────────────────────
  'library.pieces': '{count} pieces',
  'library.piecesArchived': '{count} pieces · {archived} archived',
  'library.nothingHereYet': 'nothing here yet',
  'library.adopt': 'Adopt from Browse',
  'library.newCharacter': 'New character',
  'library.newSetting': 'New setting',
  'library.newProp': 'New prop',
  'library.newBeat': 'New beat',
  'library.actions': '{name} — actions',
  'library.editCharacter': 'Edit character',
  'library.editSetting': 'Edit setting',
  'library.editProp': 'Edit prop',
  'library.editBeat': 'Edit beat',
  'library.archive': 'Archive',
  'library.make': 'Make',
  'library.tryAgain': 'Try again',
  'library.archived': 'Archived',
  'library.archiveSection': 'Archive',
  'library.archivedNote':
    'Archiving takes a piece off the shelf without deleting it — any story that uses it keeps working exactly as before. Only a piece nothing still uses can be deleted for good.',
  'library.archivedMeta': '{count} pieces · still working in your stories',
  'library.restore': 'Restore',
  'library.delete': 'Delete',
  'library.usedByStories': 'used by {count} stories',
  'library.usedByOneStory': 'used by 1 story',
  'library.stillInStories': 'Still in {count} stories, and still working in them.',
  'library.nothingUses': 'Nothing uses this one. You can delete it for good if you want to.',
  'library.noMatches': 'nothing matches',
  'library.noMatchesBody': 'Nothing here matches what you typed. Try a shorter word.',
  'library.checking': 'checking…',

  // empty kinds — an invitation with a verb, never a failure
  'empty.characters.title': 'No characters yet.',
  'empty.characters.body':
    'A character is someone a story is about — your child, their fox, the neighbour’s dog. You write who they are, and they look the same in every scene after that.',
  'empty.settings.title': 'No settings yet.',
  'empty.settings.body':
    'A setting is a place with nobody in it — a clean backdrop everyone gets placed on top of. The moonlit garden, the night harbour.',
  'empty.props.title': 'No props yet.',
  'empty.props.body':
    'A prop is a thing a character carries or a scene needs — a lantern, a tin whistle, a flower.',
  'empty.beats.title': 'No beats yet.',
  'empty.beats.body':
    'A beat is a moment more than one character shares — "Atlas hands the fox a flower". You\u2019ll make one when a story asks for it.',
  'empty.adopt': 'or adopt one from Browse',

  // the new-piece dialog
  'new.character': 'A new character',
  'new.setting': 'A new setting',
  'new.prop': 'A new prop',
  'new.beat': 'A new beat',
  'new.body': 'Give them a name first. You can write who they are, and draw them, next.',
  'new.bodyThing': 'Give it a name first. You can write what it is, and draw it, next.',
  'new.name': 'Name',
  'new.namePlaceholder': 'Sable',
  'new.create': 'Create',
  'new.nameRequired': 'A name, please.',

  // ── spending — every generation is priced before it happens ───────────────
  'cost.oneGeneration': '1 generation',
  // The month is spent. One calm line, in the place a failed draw's message lands — the
  // parent learns when they ask, never from a warning counting down at them beforehand.
  'ledger.monthSpent': "That's every picture for this month. More on {date}.",
  'spend.notNow': 'Not now',
  'spend.oneGeneration': 'Costs {gen} · about a minute',
  'make.title': 'Make this now?',
  'make.body':
    "We'll draw a model sheet — a few poses and expressions on one image. You'll see it and say whether it's right before anything else gets made from it.",
  'make.bodyPlain':
    "We'll draw it once, from what you've written. Nothing is locked until you've seen it.",
  'make.confirm': 'Make it',
  'make.thisCosts': 'This costs {gen} · about a minute',

  // ── permanent delete — reachable only from the archive ────────────────────
  'delete.title': 'Delete this for good?',
  'delete.body':
    "This permanently removes {name} and every image made for it. It can't be undone. It's deletable only because no story uses it.",
  'delete.bodyDrawings':
    "This permanently deletes {name} and their drawings. It can't be undone. It's deletable only because no story uses it.",
  'delete.keep': 'Keep it',
  'delete.confirm': 'Delete permanently',
  'delete.refusedTitle': "It's still in use",
  'delete.refusedBody':
    "{name} can't be deleted while something still uses it. It stays in the archive, and everything that uses it keeps working.",
  'delete.usedByStory': 'the story "{title}"',
  'delete.usedByRender': 'something you made',
  'delete.usedByGlobal': 'the shared shelf',
  'delete.close': 'Close',

  // ── the archive toasts ────────────────────────────────────────────────────
  'toast.undo': 'Undo',
  'toast.archivedUsed': "Archived. It's still in {count} stories and keeps working there.",
  'toast.archivedFree': '{name} is archived. You can bring it back whenever you want.',
  'toast.restored': '{name} is back on your shelf.',
  'toast.optionRemoved':
    '"{option}" removed. Any variants already made for it are still in your library.',
  'toast.deleted': '{name} is gone for good.',

  // ── character detail ──────────────────────────────────────────────────────
  'beat.cast': 'Who is in it',
  'beat.castNone': 'nobody yet',
  'beat.castCount': '{count} characters',
  'beat.castHint':
    'A beat is drawn for these characters in particular. Changing who is in it makes it a different beat, so its version goes up.',
  'beat.castNoCharacters': 'Make a character first — a beat is a moment between them.',

  'piece.kickerVersion': '{kind} · v{version}',
  'piece.kickerLocked': '{kind} · v{version} · locked',
  'piece.variantsMadeOpen': '{made} variants made · {open} open',
  'piece.identity': 'Identity',
  'piece.identityLocked': 'locked · v{version}',
  'piece.identityLead':
    'What makes them them: a few lines and, if you like, a drawing. Both are edited right here.',
  'piece.identityLeadThing':
    'What makes it what it is: a few lines and, if you like, a drawing. Both are edited right here.',
  'piece.identityEmpty':
    'Nothing written down yet. A line or two about who they are is what the drawing comes from.',
  'piece.identityHint':
    'Change this and their version goes up, and you meet them again — the lock is a choice, not a gate.',
  'piece.identityHintThing': 'Change this and its version goes up, and it gets drawn again.',
  'piece.identityEdit': 'Edit identity',
  'piece.identityEditNote': 'notes and drawing together · one save, one version',
  'piece.identityConsequence':
    'These lines and this drawing are what make {name} {name}. Save, and their version becomes v{next} and you meet them again; pictures already made stay at v{version}.',
  'piece.identityConsequenceFirst':
    'These lines and this drawing are what make {name} {name}. Save, and their version becomes v{next}.',
  'piece.identityConsequenceThing':
    'These lines and this drawing are what {name} is. Save, and its version becomes v{next}; pictures already made stay at v{version}.',
  'piece.identityConsequenceThingFirst':
    'These lines and this drawing are what {name} is. Save, and its version becomes v{next}.',
  'piece.identityQuestion': 'Who are they?',
  'piece.identityQuestionThing': 'What is it?',
  'piece.identityNotesHelp':
    'What they are, and what never changes. Poses and expressions belong to the axes, not here.',
  'piece.identityNotesHelpThing':
    'What it is, and what never changes. Ways it varies belong to the axes, not here.',
  'piece.identityCost': 'Saving is free · drawing them again costs {gen}',
  'piece.identitySave': 'Save and meet them again',
  'piece.identitySaveThing': 'Save',
  'piece.save': 'Save',
  'piece.cancel': 'Cancel',
  'piece.colourAnchor': 'Colours that must not drift',
  'piece.colourAnchorHelp': 'A black hat should stay black. Name the colours that matter.',

  // the drawing slot — the one upload, and the one sentence about it
  'piece.drawing': 'A drawing to work from',
  'piece.drawingChoose': 'Choose a drawing to work from',
  'piece.drawingAdd': 'Add a drawing',
  'piece.drawingYours': 'Your drawing',
  'piece.drawingLabel': 'Drawing',
  'piece.drawingOptional': 'optional',
  'piece.drawingNone': 'No drawing',
  'piece.drawingNeverPhoto': 'a drawing — never a photo',
  'piece.drawingReplaceShort': 'Replace',
  'piece.drawingRemoveShort': 'remove',
  'piece.drawingPrivate': 'Yours only. It never leaves your library.',
  'piece.drawingWrongType': 'A PNG, JPEG or WebP, please.',
  'piece.drawingTooLarge': 'That file is over 10 MB. A smaller one, please.',
  'piece.drawingFailed': "That didn't upload. Try again?",

  // the base-reference panel
  'piece.baseReference': 'Base reference',
  'piece.baseReferenceCrop': 'Base reference · canonical crop',
  'piece.modelSheet': 'Model sheet',
  'piece.modelSheetSplit': 'Model sheet · {count} poses split out',
  'piece.sheetSource': 'source',
  'piece.sheetNote':
    'This sheet is the moment you decided they were right. Its poses split into their starter variants; the rest get made when you ask.',
  'piece.noReferenceYet': 'Nothing drawn yet',
  'piece.generate': 'Draw them',
  'piece.generateThing': 'Draw it',
  'piece.remake': 'Remake',
  'piece.meetThem': 'Meet {name}',
  'piece.backdropNote': 'A clean backdrop plate with nobody in it. Everyone gets placed on top.',
  'piece.drawingBeforeGenerate':
    'Write a line about who they are first — the drawing comes from the words.',

  // axes
  'axes.title': 'Variant axes',
  'axes.options': '{count} options',
  'axes.addOption': 'add an option',
  'axes.addAxis': 'add a way to vary',
  'axes.removeOption': 'Remove the {axis} option {option}',
  'axes.removeAxis': 'Remove the {axis} axis',
  'axes.newOption': 'New {axis} option',
  'axes.axisActions': '{axis} — actions',
  'axes.renameAxis': 'Rename the {axis} axis',
  'axes.renameOption': 'Rename the {axis} option {option}',
  'axes.newAxis': 'What varies? (pose, season, state…)',
  'axes.hint':
    'Changing an axis never invalidates the reference — the version tracks identity only. Variants already made for an option you remove stay in your library.',
  'axes.bound': 'Up to 4 ways to vary, 8 options each.',
  'axes.tooBig':
    'That many options would describe more variants than the Studio will hold. Up to 4 ways to vary, 8 options each.',
  'axes.none': 'No ways to vary yet. Add one and the grid below fills with what you could make.',

  // the matrix
  'matrix.title': 'Variants',
  'matrix.label': "{name}'s variants — {columns} by {rows}",
  'matrix.labelOne': "{name}'s variants — {columns}",
  'matrix.hint':
    "Most of this grid is empty on purpose. An open cell isn't missing — it's something you haven't needed yet. Tap one and it gets made.",
  'matrix.cost':
    "Making a variant costs {gen}. Most of them never get made, and that's the design.",
  'matrix.blocked': 'Nothing else gets made until you say the reference is right.',
  'matrix.tapToMake': 'tap to make',
  'matrix.making': 'Making',
  'matrix.didntComeBack': "didn't come back",
  'matrix.tryAgain': 'try again',
  'matrix.fromSheet': 'from the sheet',
  'matrix.cellOpen': '{name}, {point} — make this one',
  'matrix.cellFilled': '{name}, {point} — open it',
  'matrix.cellMaking': '{name}, {point} — making',
  'matrix.cellError': "{name}, {point} — didn't come back, try again",
  'matrix.cellBlocked': '{name}, {point} — waiting until you say the reference is right',
  'matrix.thirdAxis': '{axis}: {option}',
  'matrix.makeTitle': 'Make this variant?',
  'matrix.makeBody': '{name}, {point} — drawn from their canonical crop so they stay the same.',
  'matrix.makeConfirm': 'Make it',
  'matrix.remakeTitle': 'Remake this one?',
  'matrix.remakeBody':
    "The image you have now gets replaced. You can remake again if you'd rather have the first one back, but each attempt spends a generation.",
  'matrix.remakeKeep': 'Keep this one',
  'matrix.remakeConfirm': 'Remake',
  'matrix.remakeCost': 'Remaking costs {gen}',
  'matrix.viewerVariant': 'variant · from v{version}',
  'matrix.close': 'Close',

  // remaking the reference itself
  'piece.remakeRefTitle': 'Draw {name} again?',
  'piece.remakeRefBody':
    "We'll draw a new model sheet and you'll meet them again. The variants you've already made stay in your library; if you say the new sheet is right, everything after it is drawn from that.",
  'piece.remakeRefConfirm': 'Draw them again',

  // ── meet your character ───────────────────────────────────────────────────
  'meet.kicker': 'Meet your character',
  'meet.pageTitle': 'Meet {name}',
  'meet.question': "Here's {name} — right?",
  'meet.drawnFrom':
    "Drawn from your notes: {notes} If they're right, they're locked — every scene, every story, every format uses this {name}.",
  'meet.drawnFromPlain':
    "Drawn from what you wrote. If they're right, they're locked — every scene, every story, every format uses this {name}.",
  'meet.takeYourTime': 'Take your time. Nothing else gets made until you say.',
  'meet.yes': "Yes — that's them",
  'meet.remake': 'Draw them again',
  'meet.remakeCost': 'Drawing them again costs {gen}',
  'meet.sheetPoses': '{count} poses',
  'meet.lockedKicker': "You've met",
  'meet.lockedTitle': '{name} is locked in.',
  'meet.lockedSaying':
    "The poses on their sheet have split into their starter variants. Whatever you make from here, it'll be the same {name}.",
  'meet.seeVariants': 'See their variants',
  'meet.backToLibrary': 'Back to your library',
  'meet.lockedNote':
    "Change their identity notes later and their version goes up and this moment opens again. The lock isn't a door — you can always change your mind.",
  'meet.lockedToast':
    '{name} is locked. Their starter variants are splitting out of the sheet now.',
  'meet.splitting': 'Splitting the sheet into their starter variants…',
  'meet.remakeRecommendedTitle': 'This sheet came out awkward',
  'meet.remakeRecommendedBody':
    'Only {usable} of {expected} poses came out cleanly — {reason} Drawing it again usually fixes it, and you can still say this one is right if you like it.',
  'meet.remakeDialogTitle': 'Draw {name} again?',
  'meet.remakeDialogBody':
    "We'll draw a new model sheet and you'll see them again. If you'd rather change their notes first, do that — the drawing comes from the words.",
  'meet.staleTitle': '{name} has changed since this was drawn',
  'meet.staleBody':
    'You edited who they are, so this sheet is of who they used to be. Draw them again and meet the new one — nothing already made goes away.',
  'meet.staleAction': 'Draw them again',
  'meet.nothingToMeet': "There's nothing to meet yet",
  'meet.nothingToMeetBody': 'Draw {name} first, and this is where you decide whether it is them.',
  'meet.notACharacter': 'Only a character has a moment like this.',

  // ── stories home ──────────────────────────────────────────────────────────
  'stories.title': 'Your stories',
  'stories.count': '{count} stories',
  'stories.countOne': '1 story',
  'stories.new': 'New story',
  'stories.bring': 'Bring a story',
  'stories.inProgress': 'In progress',
  'stories.made': 'Made',
  'stories.outputs': '{count} outputs',
  'stories.kindDraft': 'Story · draft',
  'stories.kindMade': 'Story · made',
  'stories.kindMadeAs': 'Story · made · {format}',
  'stories.kindArchived': 'Story · archived',
  'stories.noScenes': 'No scenes yet',
  'stories.madeOf': '{scenes} scenes · {cast}',
  'stories.madeOfOne': '1 scene · {cast}',
  'stories.madeOfNoCast': '{scenes} scenes',
  'stories.nothingComposed':
    'Named, and nothing composed yet — you\u2019ll start it when you\u2019re ready.',
  'stories.statusNothing': 'Nothing composed yet',
  'stories.statusComposing': 'Composing scenes',
  'stories.statusReady': 'Ready to make',
  'stories.continue': 'continue',
  'stories.composeFirst': 'compose the first scene',
  'stories.makeIt': 'make it',
  'stories.railNothing': 'Nothing is being made right now.',
  'stories.railScenes': '{done} of {total}',
  'stories.meet': 'meet',
  'stories.noLibraryHere':
    'Nothing from your library shows up here. This page is only what\u2019s being made and what\u2019s waiting on you; the library keeps to its own tab.',
  'stories.emptyTitle': 'No stories yet.',
  'stories.emptyBody':
    'A story is a handful of scenes made from pieces you already have. Name it first — composing it is free, and only the pictures cost.',
  'stories.newTitle': 'A new story',
  'stories.newBody': 'Give it a title. You can change it whenever you like.',
  'stories.newName': 'Title',
  'stories.newPlaceholder': 'The night harbour',
  'stories.archived': 'Archived stories',
  'stories.archivedNote':
    'An archived story is off the list but not deleted — anything it made keeps working. Only a story nothing was made from can be deleted for good.',
  'stories.deleteBody':
    "This permanently removes {name} and everything composed for it. It can't be undone. It's deletable only because nothing was made from it.",

  // ── the picture page — the composer, on the story page and in format mode ──
  'compose.stage': 'The picture, as it will be drawn',
  'compose.stageEmpty': 'Add a setting, then who’s in it.',
  'compose.itemLabel': '{name} — {state}. Arrows move it, shift and arrows resize it.',
  'compose.itemSelected': 'selected',
  'compose.itemUnselected': 'tap to select',
  'compose.placedByRole': 'placed by role · nudge them if you like',
  'compose.roleBackdrop': 'backdrop · behind',
  'compose.roleCharacter': 'character · mid',
  'compose.roleProp': 'prop · front',
  'compose.source': 'Pull from your library',
  'toast.region': 'Messages',
  'compose.sources': 'Pieces you can place',
  'compose.pickAdd': 'add',
  'compose.pickPlaced': 'placed',
  'compose.pickBlockedCharacter': 'meet {name} first',
  'compose.pickBlockedPiece': 'draw {name} first',
  'compose.pickMaking': 'being drawn',
  // The rail's variant picker (`studio/13` §10.2) says the story area's words, not the
  // library's: here a picture is *drawn*, and *make* belongs to the library's matrix.
  'compose.variantDraw': 'draw',
  'compose.variantCost': 'a made one is free · a new one costs {gen}',
  'compose.variantDone': 'Done',
  // The stage's selected piece (`studio/16`): the same picker as the rail, for THIS placement.
  'compose.layerOrder':
    'backdrop behind · characters mid · props front — placed by role, nudge them if you like',
  'compose.selectedLabel': 'selected',
  'compose.placementNth': '{n} placement',
  'compose.placementBack': 'further back',
  'compose.placementFront': 'in front',
  'compose.placedTwice': '×{n} · pick on the stage',
  'compose.inThisPicture': 'in this picture',
  'compose.inThisPlacement': 'in this placement · {n} of {of}',
  'compose.removePiece': 'Take {name} out of this picture',
  'compose.adjustPieces': 'adjust the pieces',
  'compose.showPicture': 'show the picture',
  'compose.layersOf': '{count} layers · all of them yours already',
  'compose.layersNothingNew':
    'Every layer here is already in your library — this picture drew nothing new.',
  'compose.layerBackdrop': 'behind',
  'compose.layerCharacter': 'mid',
  'compose.layerProp': 'front',
  'compose.layerForeground': 'front',
  'compose.flatComposite': 'composite',
  'compose.flatRelit': 'relit',
  'compose.pictureCost': 'This picture costs {gen}',
  'compose.pictureCostMany': 'This picture costs {count} generations',
  'compose.pictureCostNone': 'Everything this picture uses is already made',

  // ── the story page — a book, open at one beat ─────────────────────────────
  'story.kicker': 'A story · {count} beats',
  'story.kickerOne': 'A story · 1 beat',
  // Each of these has a `…One` twin, chosen by `tCount`. English inflects the noun and
  // nothing else, so two twins differ by one letter and the third is its base repeated —
  // the twin exists so the mechanism is uniform and every language states its own forms.
  'story.meta': '{ready} pictures ready · {making} making',
  'story.metaOne': '{ready} picture ready · {making} making',
  'story.metaNoneMaking': '{ready} pictures ready',
  'story.metaNoneMakingOne': '{ready} picture ready',
  'story.metaFailed': '{failed} didn’t come back',
  'story.metaFailedOne': '{failed} didn’t come back',
  'story.metaNothingYet': 'nothing drawn yet',
  'story.backToStories': 'back to your stories',
  'story.statusNotDrawn': 'This picture isn’t drawn yet',
  'story.statusMaking': 'Making',
  'story.statusReady': 'Ready',
  'story.statusFailed': 'Didn’t come back',
  'story.formats': 'Formats',
  'story.draw': 'Draw this picture',
  'story.drawAgain': 'Draw it again',
  'story.lineLabel': 'The line for beat {index}',
  'story.lineHelp': 'the line you’ll read aloud',
  'story.viewLabel': 'How to see this picture',
  'story.viewPicture': 'Picture',
  'story.viewLayers': 'Layers',
  'story.book': 'The book',
  'story.bookMeta': 'cover · {count} beats · the end',
  'story.bookLabel': 'The book, spread by spread',
  'story.cover': 'cover',
  'story.coverLabel': 'Cover — {title}',
  'story.leafLabel': 'Beat {index} — {line}',
  'story.leafLabelOpen': 'Beat {index} — {line}, open now',
  'story.leafMaking': 'making',
  'story.leafFailed': 'didn’t come back',
  'story.theEnd': 'The end.',
  'story.theEndCap': 'the end',
  'story.theEndLabel': 'The last page — The end',
  'story.beats': 'Beats',
  'story.beatsInOrder': '{count} · in order',
  'story.beatsLabel': 'Beats in reading order',
  'story.reorder': 'Reorder — beat {index}. Use arrow up and arrow down.',
  'story.moved': 'Beat moved to {index} of {total}.',
  'story.beatActions': 'Beat {index} actions',
  'story.duplicate': 'Duplicate beat',
  'story.duplicated': 'Beat {index} duplicated. The copy is the same picture and costs nothing.',
  'story.sameAs': 'same picture as beat {index} — move something or change a variant',
  'story.useOnCover': 'Use this picture on the cover',
  'story.onCover': 'on the cover',
  'story.remove': 'Remove from the story',
  'story.removeTitle': 'Take this beat out?',
  'story.removeBody':
    'The picture it drew stays in your library and costs nothing to bring back — only the beat’s place in the story goes.',
  'story.removeConfirm': 'Take it out',
  'story.rowReady': 'Ready',
  'story.rowPlaced': 'Placed, not drawn',
  'story.rowEmpty': 'Nothing placed yet',
  'story.rowMaking': 'Making',
  'story.rowFailed': 'Didn’t come back',
  'story.retry': 'try again',
  'story.thumbOpen': 'open',
  'story.thumbEmpty': 'empty',
  'story.addBeat': 'Add a beat',
  'story.addBeatFree': 'adding one is free',
  'story.note':
    'This page is the story itself: every beat has a line and a picture. Nothing about a format lives here — the accordion, the cards, the tunnel book each have a canvas of their own.',
  'story.drawTitle': 'Draw this picture?',
  'story.drawBodyFree':
    '{pieces} get drawn into one picture. Every piece it uses is already made, so nothing new gets drawn of anybody.',
  'story.drawBodyCost':
    '{pieces} get drawn into one picture. {count} of them have not been drawn yet, so those get made first.',
  'story.drawGo': 'Draw it',
  'story.drawNotYet': 'Not yet',
  'story.pieces': 'The pieces',
  'story.and': 'and',
  'story.openBeat': 'open',
  'story.beatReady': 'ready',
  'story.beatMaking': 'making',
  'story.beatOpen': 'open',
  'story.cast': 'The cast this story uses',

  // relight — the optional harmonize pass, always a choice
  'relight.action': 'Relight',
  'relight.title': 'Relight this picture?',
  'relight.body':
    'A light pass over the picture that settles the lighting and the shadows between the pieces. Nothing moves and nobody changes — and the plain composite is kept either way.',
  'relight.confirm': 'Relight it',
  'relight.cost': 'Relighting costs {gen}',
  'relight.working': 'Relighting',
  'relight.done': 'Relit',
  'relight.revert': 'back to the plain composite',
  'relight.failed': "That didn't come back — the plain composite is still here.",

  // ── formats — the picker, and one canvas per medium ───────────────────────
  'formats.kicker': '{story} · formats',
  'formats.title': 'What should this become?',
  'formats.meta': '{count} beats · {ready} pictures ready · every format starts from the story',
  'formats.pick': 'Which format',
  'formats.open': 'open',
  'formats.opened': 'Open',
  'formats.ages': 'ages {min}–{max}',
  'formats.noIllustration': 'no picture of it yet',
  'formats.stateNotStarted': 'not started',
  'formats.stateStartsFromStory': 'starts from the story',
  'formats.stateTakesOne': 'takes one scene',
  'formats.stateScenes': '{count} scenes',
  'formats.stateAllFromStory': 'all from the story',
  'formats.stateChanged': '{count} changed here',
  'formats.stateNothingToChange': 'nothing to change',
  'formats.railBeats': 'Beats',
  'formats.railFormats': 'Formats',
  'formats.backToStory': 'back to the story',
  'formats.railNotStarted': 'not started',
  'formats.railFromStory': 'from the story',
  'formats.railStoryItself': 'the story itself',
  'formats.railChanged': '{count} changed',
  'formats.floorFree': 'Adapting is free — until you change a picture',
  'formats.floorNeedsOne': 'One thing needs you in {format}',
  'formats.floorNeedsMany': '{count} things need you in {format}',
  'formats.emptyTitle': 'Nothing to make it into yet.',
  'formats.emptyBody':
    'The keepsakes open one at a time as they are ready. Nothing is hidden behind a wall — there is simply nothing published yet.',
  'formats.needBeats': 'Add a beat first, and this page fills with what the story could become.',
  'formats.headerState': '{format} · {count} scenes',
  'formats.make': 'Make {format}',
  'formats.canvasTitle': 'Scenes for {format}',
  'formats.canvasMeta': '{count} · {derived} from the story · {changed} changed here',
  'formats.canvasMetaAll': '{count} · all from the story',
  'formats.canvasNote':
    'Every scene starts as the story’s picture. Change one here and it stops following the story; the rest keep following. Going back to the story’s picture is always free.',
  'formats.listLabel': 'Scenes in this format, in order',
  'formats.reorder': 'Reorder — scene {index}. Use arrow up and arrow down.',
  'formats.moved': 'Scene moved to {index} of {total}.',
  'formats.beatFrom': 'beat {index}',
  'formats.wasBeat': 'was beat {index}',
  'formats.addedHere': 'added here',
  'formats.badgeDerived': 'From the story',
  'formats.badgeOverridden': 'Changed here',
  'formats.badgeReframed': 'Changed here · re-framed',
  'formats.badgeStale': 'Changed here · the story changed too',
  'formats.badgeOrphaned': 'This beat is gone from the story',
  'formats.badgeAdded': 'Added here',
  'formats.makingInStory': 'Making, in the story',
  'formats.making': 'Making',
  'formats.notDrawnYet': 'not drawn yet',
  'formats.failed': 'didn’t come back',
  'formats.openScene': 'open',
  'formats.followAgain': 'follow the story again',
  'formats.menuFollowAgain': 'Follow the story again',
  'formats.takeNew': 'take the story’s new picture',
  'formats.keepMine': 'keep mine',
  'formats.keepOwn': 'keep it as its own',
  'formats.menuKeepOwn': 'Keep it as its own',
  'formats.removeHereToo': 'remove it here too',
  'formats.menuReframe': 'Re-frame for this format',
  'formats.menuAdjustFrame': 'Adjust the frame',
  'formats.menuChange': 'Change this one here',
  'formats.menuDuplicate': 'Duplicate',
  'formats.menuRemove': 'Remove from this format',
  'formats.sceneActions': 'Scene {index} actions',
  'formats.addFromStory': 'Add a beat from the story',
  'formats.addFromStoryPick': 'Which beat',
  'formats.addNew': 'Add a new scene',
  'formats.addCost': 'from the story is free · a new scene costs {gen}',
  'formats.whatItWillBe': 'What it will be',
  'formats.paperModel': 'a paper model',
  'formats.modelLabel': 'A paper model of {format}. Drag to turn it.',
  'formats.modelFallback': 'the paper model loads in the browser',
  'formats.modelNoForm': 'no paper model for this shape yet',
  'formats.howOpen': 'How open',
  'formats.foldClosed': 'Closed',
  'formats.foldFolded': 'Folded',
  'formats.foldOpening': 'Opening',
  'formats.foldOpen': 'Open',
  'formats.dragToTurn': 'drag to turn it',
  'formats.whereCard': 'Where the top card is',
  'formats.cardInStack': 'In the stack',
  'formats.cardSlidingOut': 'Sliding out',
  'formats.cardBack': 'The back',
  'formats.sizesNotFinal': 'Sizes not final.',
  'formats.sizesNotFinalModel':
    'The model’s proportions are right; its sizes aren’t yet — they settle when the print partner does.',
  'formats.factPanels': 'Panels',
  'formats.factSpreads': 'Spreads',
  'formats.factCards': 'Cards',
  'formats.factPlanes': 'Planes',
  'formats.factScenes': 'Scenes',
  'formats.factTakes': 'Takes',
  'formats.factRange': '{min} – {max} · best at {ideal}',
  'formats.factPictures': 'Pictures',
  'formats.factPicturesFlat': 'flat print · one scene each',
  'formats.factPicturesLayers': 'the layers · one scene, as planes',
  'formats.factLines': 'Lines',
  'formats.factLinesUnder': 'under each picture',
  'formats.factLinesNone': 'none — the planes are cut, not captioned',
  'formats.factPage': 'Page',
  'formats.factPageSquare': 'square · line left, picture right',
  'formats.fitOk': '{count} scenes fit.',
  'formats.fitOkOne': '1 scene fits.',
  'formats.fitWaitingOne': 'One is waiting on you.',
  'formats.fitWaitingMany': '{count} are waiting on you.',
  'formats.fitTooFew': 'It holds at least {need} scenes; you have {have}.',
  'formats.fitTooMany': 'It holds up to {holds} scenes; you have {have}. Take some out here.',
  'formats.fitIdeal': 'It reads best at about {ideal}.',
  'formats.fitAddInStory': 'Add beats in the story if you’d like more.',
  'formats.remove': 'Remove this format',
  'formats.removeNoTouch': 'doesn’t touch the story',
  'formats.removeTitle': 'Remove {format}?',
  'formats.removeBodyChanged':
    'The {count} scenes you changed here go. The story and its pictures stay exactly as they are — you can start {format} again any time.',
  'formats.removeBodyOne':
    'The one scene you changed here goes. The story and its pictures stay exactly as they are — you can start {format} again any time.',
  'formats.removeBodyNone':
    'Nothing was changed here, so nothing is lost. The story and its pictures stay exactly as they are — you can start {format} again any time.',
  'formats.removeKeep': 'Keep it',
  'formats.removeConfirm': 'Remove',
  'formats.removed': '{format} removed. The story is untouched.',
  'formats.refusedTitle': 'This one stays.',
  'formats.refusedBody':
    'This one was made into a keepsake, so it stays — that way what your order was made from is always on the record.',
  'formats.refusedOk': 'All right',
  'formats.startMeta': 'not started',
  'formats.startBody':
    'This format starts from the story’s {count} beats — each beat becomes a scene. Starting is free and doesn’t touch the story.',
  'formats.startBodyHero':
    'This format takes one scene; its layers become planes. It starts on the fullest beat — you can pick another after.',
  'formats.start': 'Start from the story',
  'formats.starting': 'Starting…',
  'formats.bookTitle': 'Pages for {format}',
  'formats.bookMeta': '{count} spreads · all from the story',
  'formats.bookNote':
    'There’s nothing to change here: the printed book is the story itself. If a beat should be different, change it in the story — the book follows.',
  'formats.bookCover': 'Cover · the title and beat {index}’s picture',
  'formats.bookCoverNone': 'Cover · the title, and the first picture once one is drawn',
  'formats.changeInStory': 'change it in the story',
  'formats.coverRow': 'c',

  // the composer in format mode — the same picture page, framed for a format
  'formats.composerKicker': '{format} · scene {index}',
  'formats.composerTitle': 'This scene',
  'formats.composerFramed': 'framed for {format} · {w}:{h}',
  'formats.composerBack': 'back to {format}',
  'formats.composerNote':
    'What you change here is {format}’s only — the story’s beat stays exactly as it is.',
  'formats.drawOverride': 'Draw this scene',
  'formats.drawOverrideAgain': 'Draw it again',
  'formats.drawOverrideTitle': 'Draw this scene?',
  'formats.drawOverrideBodyFree':
    '{pieces} get drawn into one picture, for {format} only. Every piece it uses is already made, so nothing new gets drawn of anybody.',
  'formats.drawOverrideBodyCost':
    '{pieces} get drawn into one picture, for {format} only. {count} of them have not been drawn yet, so those get made first.',
  'formats.sceneStatusNotDrawn': 'This scene isn’t drawn yet',
  'formats.sceneStatusFollows': 'Following the story',

  // ── make — per format: the print proof, and the order handoff ─────────────
  'proof.kicker': 'Make · {format}',
  'proof.title': 'The print proof',
  'proof.meta': 'cover + {count} panels · every picture ready · pinned to the story’s versions',
  'proof.metaSpreads':
    'cover + {count} spreads · every picture ready · pinned to the story’s versions',
  'proof.metaPieces': '{count} pieces · every picture ready · pinned to the story’s versions',
  'proof.metaWaiting': '{count} pictures still being made',
  'proof.metaWaitingOne': 'one picture still being made',
  'proof.metaNeedsYou': 'one scene needs you in the canvas',
  'proof.marksLabel': 'Show print marks',
  'proof.asItLooks': 'As it looks',
  'proof.withMarks': 'With print marks',
  'proof.note':
    'This is exactly what gets printed: every panel, every line, in order. Check the words — the pictures are the story’s and don’t change here.',
  'proof.noteSpreads':
    'This is exactly what gets printed: every spread, every line, in order. Check the words — the pictures are the story’s and don’t change here.',
  'proof.sizesNotFinal':
    'The proportions are right; the paper size and weight settle when the print partner does. You’ll see the final proof before anything is ordered.',
  'proof.label': '{format}, laid flat',
  'proof.labelSpreads': '{format}, spread by spread',
  'proof.labelPieces': '{format}, piece by piece',
  'proof.fold': 'fold',
  'proof.bleed': 'bleed',
  'proof.cover': 'cover',
  'proof.theEnd': 'the end',
  'proof.panelFrom': 'from the story',
  'proof.panelChanged': 'changed here',
  'proof.panelAdded': 'added here',
  'proof.panelMaking': 'making',
  'proof.panelNeedsYou': 'needs you',
  'proof.noWords': 'no words yet',
  'proof.pageNumber': 'Page {index}',
  'proof.cardNumber': 'Card {index}',
  'proof.planeNumber': 'Plane {index} · {role}',
  'proof.backToChange': 'back to {format} to change something',
  'proof.summaryPanels':
    '{count} panels · flat print · one side · no new pixels — all of it is the story’s pictures',
  'proof.summarySpreads':
    '{count} spreads · flat print · no new pixels — all of it is the story’s pictures',
  'proof.summaryPieces': '{count} pieces · no new pixels — all of it is the story’s pictures',
  'proof.whatArrives': 'What arrives',
  'proof.patara': 'the Atlas Nook Patara line',
  'proof.theThing': 'The thing',
  'proof.panels': 'Panels',
  'proof.spreads': 'Spreads',
  'proof.pieces': 'Pieces',
  'proof.panelsCount': '{count} · the cover + {scenes} scenes',
  'proof.spreadsCount': '{count} · one per scene, and a cover',
  'proof.piecesCount': '{count}',
  'proof.paper': 'Paper',
  'proof.paperNote': 'sizes once final',
  'proof.when': 'When',
  'proof.whenNote': 'printed and posted',
  'proof.after': 'After',
  'proof.afterNote':
    'Change the story and this one doesn’t change — it’s pinned to the versions it was made from.',
  'proof.priceLine': '{price} · ships in {lead}',
  'proof.priceNote':
    'The price and lead time are the catalogue’s; the print partner confirms and charges at checkout. The Studio never takes a payment.',
  'proof.goesToCheckout': 'goes to checkout',
  'proof.order': 'Order {format}',
  'proof.orderTitle': 'Continue to checkout?',
  'proof.orderBody':
    'You’ll leave the Studio and pay on our print partner’s site. Only the print files go with the order — nothing about your child does.',
  'proof.orderCost': 'the price is shown there',
  'proof.orderNotYet': 'Not yet',
  'proof.orderGo': 'Go to checkout',
  'proof.checkoutNotSetUp': 'Ordering opens with the Patara store — not yet.',
  'proof.orderFailed': 'That didn’t go through. Nothing was ordered.',
  'proof.disabledWaiting': '{count} pictures are still being made',
  'proof.disabledWaitingOne': 'one picture is still being made',
  'proof.disabledOrphan': 'one scene needs you in the canvas first',
  'proof.disabledFit': 'this one doesn’t fit yet — change it in the canvas',
  'proof.free': 'The proof is free · nothing new gets drawn',
  'proof.railBack': 'back to {format}',
  'proof.railTitle': '{format} · {story}',
  'proof.railPanels': 'Panels',
  'proof.railCast': 'The cast',
  'proof.railReady': 'ready',
  'proof.madeNote':
    'This one exists — {count} pieces, ready. Ordering it again uses the same files.',

  // ── browse — the shared shelf ─────────────────────────────────────────────
  'browse.curated': '{count} pieces · curated by Atlas Nook',
  'browse.curatedKind': '{kind} · curated',
  'browse.filter': 'Filter the shelf',
  'browse.mine': 'In your library',
  'browse.inYourLibrary': 'In your library',
  'browse.notDrawn': 'not drawn yet',
  'browse.useAsIs': 'Use as is',
  'browse.makeItMine': 'Make it mine',
  'browse.openIt': 'open it',
  'browse.forked': '{name} is yours now. Draw them when you\u2019re ready.',
  'browse.emptyTitle': 'The shared shelf is empty.',
  'browse.emptyBody':
    'Starters are curated by Atlas Nook and appear here as they are made. Your own library is where your pieces live.',
  'browse.noMatchesTitle': 'Nothing here matches that.',
  'browse.noMatchesBody': 'Try turning a filter off.',
  'browse.shared': 'Shared',

  // ── placeholder surface titling (studio/02–05 replace this) ───────────────
  'surface.opening': 'Opening…',
  'surface.library': 'Your library',
  'surface.piece': 'A piece',
  'surface.pieceKicker': 'Library',
  'surface.meet': 'Meet your character',
  'surface.meetKicker': 'Library',
  'surface.stories': 'Your stories',
  'surface.storiesKicker': 'Stories',
  'surface.story': 'A story',
  'surface.storyKicker': 'Stories',
  'surface.compose': 'Scene composer',
  'surface.composeKicker': 'Stories',
  'surface.preview': 'Preview',
  'surface.previewKicker': 'Stories',
  'surface.make': 'Make',
  'surface.makeKicker': 'Stories',
  'surface.browse': 'Starters you can use or fork',
  'surface.browseKicker': 'The shared shelf',
  'surface.intake': 'What have you got?',
  'surface.intakeKicker': 'A new story',
  'surface.placeholder': 'This surface is not built yet — the frame is.',
  'surface.notFound': "There's nothing at this address",
  'surface.notFoundBody': 'The link may be old, or the piece may have been archived.',
  'surface.notFoundAction': 'Go to your library',
} as const;

/** Every key the Studio can say. `tr.ts` must cover all of them. */
export type MessageKey = keyof typeof en;
export type Catalog = Record<MessageKey, string>;
